import {
    RemixiconComponentType, RiAlarmLine, RiChat1Line,
    RiExchangeDollarFill, RiFileList2Line, RiFlashlightLine,
    RiLayout6Line, RiLayoutColumnLine, RiListCheck, RiListSettingsLine,
    RiMailLine, RiPaletteLine, RiSettings4Line,
    RiShareForwardLine, RiSparklingLine, RiStackLine, RiTeamLine,
    RiThumbUpLine, RiTimeLine, RiUserLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { IconType } from "react-icons";
import { Settings, useGetSettingsQuery } from "../../__generated__/api";
import useRouting, { useParams } from "../../contexts/Routing";
import { ErrorRemote } from "../error/Error";
import Styles from "./Administrator.module.scss";

import ChannelTagSettings from "./sections/channels/channel-tags/ChannelTagSettings";
import GlobalChannelSettings from "./sections/channels/channel-settings/ChannelSettings";

import SLASettings from "./sections/sla/SLAV3";

import Customization from "./sections/customization/CustomizationV3";
import Feedback from "./sections/feedback/FeedbackV3";
import ActionForm from "./sections/request-form/Actions";
import RequestTypes from "./sections/request-types/RequestTypesV3";
import TicketStates from "./sections/ticket-states/TicketStates";

import EmailSettings from "./sections/channels/email/EmailV3";
import Forwarding from "./sections/channels/forwarding/ForwardingV3";
import { ChannelSettingsV3 as WebChatSettings } from "./sections/channels/web-chat/WebChat";
import CustomField from "./sections/custom-field/CustomField";
import QueueSettings from "./sections/queues/QueuesV3";
import RequestForm from "./sections/request-form/RequestFormV3";

import ChatSettings from "./sections/chat/ChatV3";
import TimeSettings from "./sections/time/Time";
import UserGroupSettings from "./sections/user-groups/UserGroups";
import UsersSettings from "./sections/users/UsersV3";
import WorkflowServicesSettings from "./sections/workflow-services/ServicesV3";

import ChannelInvites from "./sections/channel-invites/ChannelInvites";
import Home from "./sections/home/HomeV3";

import ArtificialIntelligence from "./sections/artificial-intelligence/ArtificialIntelligenceV3";
import Billing from "./sections/billing/BillingV3";

import PortalSettings from "./sections/portal/PortalV3";

import AuditLogs from "./sections/audit-logs/AuditLogsV3";

import usePortal, { Entitlements } from "../../contexts/PortalContext";
import { SettingsPageContext, SettingsProps } from "./sections/base";

import { ReactComponent as AppsLineIntegration } from "../../assets/icons/apps-line-integrations.svg";
import { ReactComponent as GroupsList } from "../../assets/icons/groups-list.svg";
import Button from "../../components/theme/button/Button";
import RouterLink from "../../components/theme/button/RouterLink";

import { SettingRegistryProvider } from "../../components/theme/settings/base";
import TopPanel from "../../components/top-panel/TopPanel";
import ChannelsTable from "./sections/channels/channel-list/ChannelList";
import ThemeIcon from "../../components/theme/icon/Icon";
import GettingStarted from "./sections/home/GettingStarted";

interface Section {
    title?: string;
    name?: string;
    slug: string;
    page?: (props: SettingsProps) => ReactNode;
    icon?: IconType | RemixiconComponentType | typeof AppsLineIntegration;
    featureFlag?: string;
    entitlementId?: string;
    isWaitLoad?: boolean;
    backSlug?: string;
    shouldShowGettingStarted?: boolean;
}
interface Category {
    name: string;
    sections: Section[]
}

const SECTIONS: Category[] = [
    {
        name: "Portal Settings",
        sections: [
            {
                slug: "home",
                page: Home,
                shouldShowGettingStarted: false,
            },
            {
                title: "General Portal Settings",
                name: "General Portal Settings",
                slug: "portal",
                page: PortalSettings,
                icon: RiSettings4Line,
            },

            {
                name: "Users",
                title: "Users",
                slug: "users",
                page: UsersSettings,
                icon: RiUserLine,
            },
            {
                title: "Tokens",
                slug: "tokens",
                page: UsersSettings,
            },
            {
                name: "Groups",
                title: "Groups",
                slug: "user-groups",
                page: UserGroupSettings,
                icon: RiTeamLine,
            },

            {
                title: "Time Settings",
                name: "Time",
                slug: "time",
                page: TimeSettings,
                icon: RiTimeLine,
            },

            {
                slug: "billing",
                name: "Billing",
                page: Billing,
                title: "Billing",
                icon: RiExchangeDollarFill,
                shouldShowGettingStarted: false,
            },
            {
                slug: "billing-success",
                page: Billing,
                shouldShowGettingStarted: false,
            },
            {
                title: "Create Plan",
                slug: "billing-checkout",
                page: Billing,
                shouldShowGettingStarted: false,
            },
            {
                slug: "billing-modify",
                page: Billing,
                title: "Edit Plan",
                backSlug: "billing",
                shouldShowGettingStarted: false,
            },

            {
                name: "Audit logs",
                title: "Audit logs",
                slug: "audit-logs",
                page: AuditLogs,
                icon: RiListSettingsLine,
                entitlementId: Entitlements.KEYS.AUDIT_LOGS,
                featureFlag: "frontend.audit-logs.isEnabled",
                shouldShowGettingStarted: false,
            },

        ],
    },

    {
        name: "Workspace Setup",
        sections: [

            {
                title: "Request Types",
                name: "Request Types",
                slug: "request-types",
                page: RequestTypes,
                icon: RiFileList2Line,
            },
            {
                title: "Custom Fields",
                slug: "custom-fields",
                page: RequestTypes,
                icon: RiFileList2Line,
            },

            {
                title: "Request Type",
                slug: "request-forms",
                page: RequestForm,
                shouldShowGettingStarted: false,
            },

            {
                title: "Custom Field",
                slug: "custom-field",
                page: CustomField,
            },
            {
                name: "Queues",
                title: "Queues",
                slug: "queues",
                page: QueueSettings,
                icon: RiStackLine,
            },

            {
                name: "Automations",
                title: "Automations",
                slug: "workflow-actions",
                page: ActionForm,
                icon: RiFlashlightLine,
            },
            {
                title: "Automations",
                slug: "workflow-actions",
                page: ActionForm,
                icon: RiFlashlightLine,
            },
            {
                name: "Columns",
                title: "Columns",
                slug: "ticket-states",
                page: TicketStates,
                icon: RiLayoutColumnLine,
            },

            {
                name: "Integrations",
                title: "Integrations",
                slug: "workflow-services",
                page: WorkflowServicesSettings,
                icon: AppsLineIntegration,
            },
            {
                title: "",
                slug: "workflow-service",
                page: WorkflowServicesSettings,
                backSlug: "workflow-services",
                shouldShowGettingStarted: false,
            },

            {
                title: "Customization",
                name: "Customization",
                slug: "customization",
                page: Customization,
                icon: RiPaletteLine,
            },

            {
                name: "SLA",
                title: "Service Level Agreements (SLA)",
                slug: "sla",
                page: SLASettings,
                icon: RiAlarmLine,
            },

            {
                title: "Forwarding",
                name: "Forwarding",
                slug: "forwading",
                page: Forwarding,
                icon: RiShareForwardLine,
            },

            {
                title: "Feedback",
                name: "Feedback",
                slug: "feedback",
                page: Feedback,
                icon: RiThumbUpLine,
            },

            {
                name: "Artificial Intelligence",
                title: "Artificial Intelligence",
                slug: "ai",
                page: ArtificialIntelligence,
                icon: RiSparklingLine,
                entitlementId: Entitlements.KEYS.ARTIFICIAL_INTELLIGENCE,
            },
        ],
    },
    {
        name: "Channels",
        sections: [

            {
                title: "Channel List",
                name: "Channel List",
                slug: "channels",
                page: ChannelsTable,
                icon: RiListCheck,
            },

            {
                title: "Channel Settings",
                name: "Channel Settings",
                slug: "channel-settings",
                page: GlobalChannelSettings,
                icon: RiListSettingsLine,
                backSlug: "channels",
            },

            {
                title: "Channel Groups",
                name: "Channel Groups",
                slug: "channel-groups",
                page: ChannelTagSettings,
                icon: GroupsList,
            },
            {
                title: "Channel Groups",
                slug: "channel-tags",
                page: ChannelTagSettings,
            },

            {
                title: "Chat",
                slug: "chat",
                page: ChatSettings,
                backSlug: "channels",
            },

            {
                title: "Channel Templates",
                name: "Channel Templates",
                slug: "channel-invites",
                page: ChannelInvites,
                icon: RiLayout6Line,
            },

        ],
    },
    {
        name: "Communication",
        sections: [
            {
                title: "Web Chat",
                name: "Web Chat",
                slug: "web-chat",
                page: WebChatSettings,
                icon: RiChat1Line,
            },

            {
                title: "Email Settings",
                name: "Email",
                slug: "channels-emails",
                page: EmailSettings,
                icon: RiMailLine,
            },

        ],
    },

];

const DEFAULT = SECTIONS[0].sections[0];
const FLAT_SECTIONS = SECTIONS.flatMap(row => row.sections);

export default function Administrator(): ReactElement {
    const { Paths, historyPush } = useRouting();
    const { featureFlags, entitlementIds, useTitle } = usePortal();
    const {
        data: settingsData, loading: isLoading, error, refetch,
    } = useGetSettingsQuery();
    let { setting } = useParams(Paths.ADMINISTRATOR_SETTING);
    const adminIdSettings = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const adminIdChildSettings = useParams(Paths.ADMINISTRATOR_SETTING_ID_CHILD);

    if (!setting) {
        setting = adminIdSettings.setting ?? adminIdChildSettings.setting;
        if (!setting) {
            const defaultItem = FLAT_SECTIONS.find(row => row === DEFAULT);
            if (defaultItem) {
                historyPush(Paths.ADMINISTRATOR_SETTING, { setting: defaultItem.slug });
            }
        }
    }

    const container = setting ? FLAT_SECTIONS.find(page => page.slug === setting) : undefined;
    const Page = container ? container.page : undefined;

    useTitle(container?.title ?? "");
    const topPanelRef = useRef<HTMLDivElement>(null);

    const [title, setTitle] = useState<ReactNode | undefined>(undefined);
    const settings = useMemo(() => settingsData?.settings as Settings, [settingsData]);
    const instance = useMemo(() => ({ setTitle, settings }), [settings]);

    useEffect(() => {
        setTitle(null);
    }, [Page]);

    return (
        <SettingsPageContext.Provider value={instance}>
            <SettingRegistryProvider>
                <div className={Styles.page}>
                    <div className={Styles.sidebar}>
                        <div className={Styles["sidebar-top"]}>
                            <h4>Settings</h4>
                        </div>
                        {SECTIONS.map(group => (
                            <div key={group.name} className={Styles["sidebar-section"]}>
                                <h5 className={Styles["sidebar-subtitle"]}>
                                    {group.name}
                                </h5>

                                <div className={Styles["sidebar-links"]} role="tablist">
                                    {group.sections
                                        .filter(section => section.name
                                            && (section.featureFlag ? featureFlags.has(section.featureFlag) : true)
                                            && (section.entitlementId ? entitlementIds.has(section.entitlementId) : true))
                                        .map(section => {
                                            const Icon = section.icon;
                                            const isSelected = container?.slug === section.slug;
                                            return (
                                                <Button
                                                    key={section.slug}
                                                    type="button"
                                                    variant="none"
                                                    className={classNames(
                                                        Styles["sidebar-links-item"],
                                                        isSelected ? Styles["sidebar-links-item-selected"] : undefined,
                                                    )}
                                                    prefixIcon={Icon && <Icon />}
                                                    onClick={() => {
                                                        topPanelRef.current?.scrollIntoView({
                                                            behavior: "smooth",
                                                            block: "start",
                                                        });
                                                        setTimeout(() => {
                                                            topPanelRef.current?.scrollIntoView({
                                                                behavior: "smooth",
                                                                block: "start",
                                                            });
                                                        }, 500);

                                                        refetch();
                                                        historyPush(Paths.ADMINISTRATOR_SETTING, { setting: section.slug });
                                                    }}
                                                >
                                                    {section.name}
                                                </Button>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}

                    </div>

                    <div className={Styles.content}>
                        <TopPanel
                            title={title ?? container?.title as string}
                            prefixButton={container?.backSlug ? (
                                <RouterLink
                                    to={Paths.ADMINISTRATOR_SETTING}
                                    params={{ setting: container.backSlug }}
                                    variant="none"
                                    prefixIcon={<ThemeIcon.Back variant="secondary" />}
                                    size="small"
                                />
                            ) : undefined}
                            ref={topPanelRef}
                        >
                            {container?.shouldShowGettingStarted !== false && <GettingStarted />}
                        </TopPanel>

                        {error ? <ErrorRemote error={error} /> : undefined}
                        {!error && (
                            <div className={Styles["content-body"]}>
                                {Page ? (
                                    <Page
                                        isV3
                                        isLoading={isLoading}
                                        settings={settingsData?.settings as Settings}
                                    />
                                ) : undefined}
                            </div>
                        )}
                    </div>

                </div>
            </SettingRegistryProvider>
        </SettingsPageContext.Provider>

    );
}
